module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('section', {
    attrs: {
      "epub:type": "chapter",
      "id": "rch1",
      "title": "1. Topophrenia"
    }
  }, [_c('div', {
    staticClass: "hide"
  }), _vm._v(" "), _c('div', {
    staticClass: "hide"
  }), _vm._v(" "), _c('p', {
    staticClass: "cn"
  }, [_c('span', {
    staticClass: "hide"
  }), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_17",
      "title": "17"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/9780253037695_Contents#ch1"
    }
  }, [_c('span', {
    staticClass: "big1"
  }, [_vm._v("1")]), _c('span', {
    staticClass: "spacec1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("Topophrenia")])])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_c('i', [_c('span', {
    staticClass: "big"
  }, [_vm._v("P")]), _c('span', {
    staticClass: "small"
  }, [_vm._v("LACE")])]), _vm._v(" "), _c('span', {
    staticClass: "small"
  }, [_vm._v("SEEMS SO")]), _vm._v(" simple—commonplace, in fact—yet the concept lies at the heart not only of some rather complicated geographic theory and practice but also, arguably, of the arts, humanities, and sciences as a whole. The idea of place is crucial to spatial literary studies, unsurprisingly, and as such it deserves further attention. The fact that place can be taken for granted at times is already a sign of its conceptual ubiquity inasmuch as everything is situated in space and in or with respect to a place, but the distinctiveness of this or that place, the relationship of a place to other places and spaces, and the effects of place on persons, events, or narratives occurring or situated in a certain place are certainly worthy of further inquiry. With respect to literature in particular, place is always exerting its influence or making itself known in the text, and narratives frequently display their fundamental spatiotemporality according to the ways in which places, as well as moments in time, are represented. As such, place can be thought of as a sort of unit in literary cartography, which, like actual mapmaking, must register various types of place at the same time as it tries to account for them. Mapping makes visible places, and, in what might seem to be a circular logic, being mapped is what in many respects establishes a place "), _c('i', [_vm._v("as")]), _vm._v(" a place. Any study of literary cartography has to take into consideration, or take for granted, the problematics of place.")]), _vm._v(" "), _c('p', {
    staticClass: "h4"
  }, [_vm._v("The Problematics of Place")]), _vm._v(" "), _c('p', {
    staticClass: "noindentb"
  }, [_vm._v("In his magisterial study "), _c('i', [_vm._v("Space and Place: The Perspective of Experience")]), _vm._v(", Yi-Fu Tuan carefully sets forth the conditions under which what we think of as a place emerges from the almost inchoate or chaotic proliferation of relations associated with space. He identifies a powerfully spatiotemporal dynamic in which a place becomes distinctive and thus knowable, and this dynamic creates meaning for and gives shape to all human experience of the world. For Tuan, a discrete or recognizable portion of otherwise undifferentiated space becomes a place when it occasions a pause, a resting of the eye or the stimulation of some other sense, at which point that now discrete spot becomes imbued with value and meaning. It thus enters the province of literary art, subject to interpretation, as well as to affective appreciation and significance. As Tuan puts it, among the ways that "), _c('i', [_vm._v("place")]), _vm._v(" can be defined,")]), _vm._v(" "), _c('p', {
    staticClass: "blk"
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_18",
      "title": "18"
    }
  }, [_vm._v("place is whatever stable object catches our attention. As we look at a panoramic scene our eyes pause at points of interest. Each pause is time enough to create an image of place that looms momentarily in our view. The pause may be of such short duration and the interest so fleeting that we may not be fully aware of having focused on any particular object; we believe we have simply been looking at the general scene. Nonetheless these pauses have occurred. It is not possible to look at a scene in general; our eyes keep searching for points of rest. We may be deliberately searching for a landmark, or a feature on the horizon may be so prominent that it compels attention. . . .")])]), _vm._v(" "), _c('p', {
    staticClass: "blki"
  }, [_vm._v("Many places, profoundly significant to particular individuals and groups, have little visual prominence. They are known viscerally, as it were, and not through the discerning eye or mind. A function of literary art is to give visibility to intimate experiences, including those of place. The Grand Tetons of landscape do not require the services of literature; they advertise themselves by sheer size. Literary art can illuminate the inconspicuous fields of human care such as a Midwestern town, a Mississippi county, a big-city neighborhood, or an Appalachian hollow."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn1",
      "id": "fn1"
    }
  }, [_vm._v("1")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("Place, then, by definition is associated with a certain way of seeing that might well be called “critical,” even “literary critical,” inasmuch as interpretation, evaluation, and analysis of its meaning, functioning, and effects are presupposed the moment a given portion of space becomes recognizable as a place. If in Tuan’s view "), _c('i', [_vm._v("place")]), _vm._v(" is understood in terms of a pause, a moment of rest, then "), _c('i', [_vm._v("space")]), _vm._v(" is associated with movement. Place can then be understood as a relatively fixed, stable, and thus familiar or at least recognizable point, whereas space partakes of the mobile, dynamic, or unfamiliar. Space is likewise associated with the freedom, but also the peril, that comes from being away from home or being on the move. “From the security and stability of place, we are aware of the openness, freedom, and threat of space, and vice-versa.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn2",
      "id": "fn2"
    }
  }, [_vm._v("2")])]), _vm._v(" In this somewhat phenomenological conception, the individual subject establishes a place through his or her experience and perception while simultaneously being subject to a place and to the space, abstract or diffuse, against which it is defined. Place is therefore profoundly subjective, but it is also a form of subjection.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("A place is not quite the same as a territory, whose tortuous history of shifting relations of power and knowledge is illuminated in Stuart Elden’s brilliant genealogy "), _c('i', [_vm._v("The Birth of Territory")]), _vm._v(", but a place must also be understood as an astonishingly dense ensemble of crystallized social relations."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn3",
      "id": "fn3"
    }
  }, [_vm._v("3")])]), _vm._v(" These relations might be disclosed in the everyday experiences, attitudes, or memories of those individuals or groups who inhabit, visit, or even just know of such places, which in turn may represent a complicated skein of intersecting historical events, narratives, traumas, and so on. Schematically, we may identify a given place, a “you are here,” from which to orient ourselves in relation to other places, but the “here” in question is never merely a location in space, such as would be approximated in "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_19",
      "title": "19"
    }
  }, [_vm._v("the abstract by a set of coordinates on a Cartesian grid or even by a street address. Obviously, there is far more to a place than its mere localizability, but the very idea of localizing, of determining a locus, is essential to producing a place, not to mention experiencing and interpreting it.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In his "), _c('i', [_vm._v("Place: A Short Introduction")]), _vm._v(", Tim Cresswell acknowledges both the simplicity and the complexity of the concept while exploring the many ways in which place or places have been understood by various spatial theorists and critics. One distinction is the difference between an abstract understanding of a given locus and the richer but messier apprehension of the place as experienced or lived. Cresswell illustrates the point by observing that the geographically definitive or specific marker “40.46 degrees North, 73.58 degrees West” does not mean much to most people, but the referent of these coordinates, New York City (or, more precisely, Midtown Manhattan), conjures up a host of images for persons familiar with the area and even for persons who may only know the place from its reputation or from images in books and movies. Pressing further into the analysis of the place, one discovers narratives without limit, social history, cultural traditions, political conflicts, artistic forms, and on and on."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn4",
      "id": "fn4"
    }
  }, [_vm._v("4")])]), _vm._v(" Hence, the exact location itself is not necessarily meaningful, at least not to all, but a given place, recognized as such, contains a plenum of meaning so vast as to be nearly overwhelming for an interpreter.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("To know a place is really to know only a little about the place, since it would be impossible to achieve anything remotely approaching a complete representation of it. This has been almost comically proven by Georges Perec in his "), _c('i', [_vm._v("Attempt at Exhausting a Place in Paris")]), _vm._v(", in which the writer tries to take note of every person and incident visible to him at a bustling intersection of Place Saint-Sulpice over the course of three days."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn5",
      "id": "fn5"
    }
  }, [_vm._v("5")])]), _vm._v(" (Needless to say, perhaps, but even by limiting his inquiry to a single intersection in Paris over a single weekend, Perec can offer little more than an infinitesimal amount of detail, considering the impossibly vast array of potential data to be registered.) This is also why, in Bertrand Westphal’s "), _c('i', [_vm._v("Geocriticism")]), _vm._v(", the insoluble problem of the corpus haunts any effort to perform a comprehensive, geocentric interpretation, as I will discuss further in the next chapter."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn6",
      "id": "fn6"
    }
  }, [_vm._v("6")])]), _vm._v(" With a nearly infinite number of potential impressions of a particular place at our disposal, at what point does the geocritic feel comfortable that the body of work has achieved some level or threshold of adequate representation? Who is to say where the line would be drawn? Where does one even begin? How does one ever decide where to end?")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("One approach, certainly, involves a reliance on the subjective experience of space and place that Tuan discusses. In many respects, this remains egocentric, as opposed to Westphal’s geocentric methodology, which attempts to avoid bias through a multifocal process in which the place, not the writer or writers representing the place, is central to the investigation at all times. However, the "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_20",
      "title": "20"
    }
  }, [_vm._v("somewhat egocentric approach that focuses on a single observer’s subjective experience allows a reader to explore the place "), _c('i', [_vm._v("as")]), _vm._v(" perceived or "), _c('i', [_vm._v("as")]), _vm._v(" experienced, without necessarily straining to achieve a quasi-scientific categorical knowledge of the place."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn7",
      "id": "fn7"
    }
  }, [_vm._v("7")])]), _vm._v(" The admittedly artificial limits on a corpus or on the object under scrutiny need not be debilitating, and these limits can often prove quite constructive. Focusing on a single author or single text, for instance, can enable a range of creative interpretations that may be productive of others. Hence, a geocritical exploration might well take as its starting point a particular text and its relation to a place, whose almost unavoidable polysemy and heteroglossia will ensure that any reading of the text, place, and relations among them will exceed simple personal or autobiographical experience, as if even that were entirely possible to pin down. The problem of place, in this sense, becomes a larger problematic: it is a grand assemblage of problems to be grappled with by the reader and writer. This subjective limit then opens up a "), _c('i', [_vm._v("plus ultra")]), _vm._v(" for subsequent, if not endless, exploration.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("A key element of such geocritical explorations has been the affective geography made visible through a given subject’s own experience with and in places. In an earlier study, Tuan had coined an evocative term, "), _c('i', [_vm._v("topophilia")]), _vm._v(", in order to represent succinctly “all of the human being’s affective ties to the material environment.” As Tuan explains, these ties “differ greatly in intensity, subtlety, and mode of expression. The response to environment may be primarily aesthetic: it may then vary from the fleeting pleasure one gets from a view to the equally fleeting but far more intense sense of beauty that is suddenly revealed. The response may be tactile, a delight in the feel of air, water, earth. More permanent and less easy to express are feelings that one has toward a place because it is home, a locus of memories, and the means of gaining a livelihood.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn8",
      "id": "fn8"
    }
  }, [_vm._v("8")])]), _vm._v(" In Tuan’s joyous phenomenology, places are endowed with deeply personal and subjective meanings. They are invested with profoundly affective or emotional content for the subject that perceives, moves about, and in the broadest sense inhabits those spaces that have become demarcated and identified as places. Topophilia, or the love of place, seems an appropriate concept for one who so revels in the sensuous geographies of place, to use Paul Rodaway’s suggestive formulation."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn9",
      "id": "fn9"
    }
  }, [_vm._v("9")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Here I might also mention the suggestive notion of "), _c('i', [_vm._v("nonplace")]), _vm._v(", analyzed by Marc Augé in his influential study, "), _c('i', [_vm._v("Non-Places: Introduction to an Anthropology of Supermodernity")]), _vm._v(". Augé focused attention on transitory sites, such as airports, hotels, highways, and supermarkets, which in a sense are not so much "), _c('i', [_vm._v("places")]), _vm._v("—that is, locations imbued with meaning, dense with historical and social reference, the result of creative human endeavor, and so forth—as "), _c('i', [_vm._v("nonplaces")]), _vm._v(", uniform, homogeneous zones of transit in which humans increasingly spend their lives."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn10",
      "id": "fn10"
    }
  }, [_vm._v("10")])]), _vm._v(" Siobhan Carroll has referred to these sorts of sites as "), _c('i', [_vm._v("atopias")]), _vm._v(", spaces “antithetical to habitable place,” and she adds to the list of man-made atopias such as "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_21",
      "title": "21"
    }
  }, [_vm._v("those mentioned by Augé a number of “natural atopias,” such as the North Pole, the middle of the ocean, the desert, or outer space, although she also notes how cyberspace is frequently imagined as a somewhat positive, man-made atopia. Carroll concludes that, whether these atopias are viewed as spaces that either liberate or threaten the individual subject, they have become increasingly useful in “orientating ourselves to the sublime space of the planet and the human networks that span its surface.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn11",
      "id": "fn11"
    }
  }, [_vm._v("11")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Recently, Dylan Trigg has called into question the binary distinction between place and nonplace distinction. Drawing on Maurice Merleau-Ponty’s "), _c('i', [_vm._v("Phenomenology of Perception")]), _vm._v(", Trigg argues that “the bodily experience of place operates on several layers” and that this multidimensionality “renders our experience of place ambiguous.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn12",
      "id": "fn12"
    }
  }, [_vm._v("12")])]), _vm._v(" Moreover, the phenomenological value of the concept of the nonplace is somewhat compromised by the recognition of the affective complexities and ambiguities associated with the subjective, and especially intersubjective, experience of these apparent nonplaces, as Trigg shows how hospital waiting rooms or beds, as well as airports, can be spaces of friendship as well as of anxiety—that is, “a con-fused space, a layered space where paradoxical aspects are not resolved into a unified whole, but instead becomes emblematic of the ambiguous nature of our spatial experience more broadly.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn13",
      "id": "fn13"
    }
  }, [_vm._v("13")])]), _vm._v(" One might also take note of “the betweenness of place,” following J. Nicholas Entrikin’s analysis of the phenomenon. That is, one can best understand a place, as well as the conception of "), _c('i', [_vm._v("place")]), _vm._v(", from the point of view that is at once situated in the midst of things and attempting to represent the space, much like the way that a narrative operates, combining a subjective, localized perspective with a more speculative or theoretical overview."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn14",
      "id": "fn14"
    }
  }, [_vm._v("14")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Given these complex factors, it may be difficult to maintain a vision of place as essentially homey, familiar, or loved. Tuan’s sunny view of topophilia—quite literally sunny, it seems, since he takes as his "), _c('i', [_vm._v("Ansatzpunkt")]), _vm._v(" a personal memory of a stunning sunrise he witnessed while camping under the open skies in Death Valley, California—is delightful,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn15",
      "id": "fn15"
    }
  }, [_vm._v("15")])]), _vm._v(" yet one cannot help feeling that this perspective on space ignores the more angst-ridden or menacing features of certain places. If “home” is somehow understood to be a topophilic space, then what of the unhomely spaces in which an alienated subject experiences the cartographic anxiety or sense of bewilderment that so typifies many literary representations of space?"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn16",
      "id": "fn16"
    }
  }, [_vm._v("16")])]), _vm._v(" Like so many Dantes, exiled from our metaphysical or actual homes, we subjected subjects often find ourselves in a "), _c('i', [_vm._v("selva oscura")]), _vm._v(", quite unable to take pleasure in the visceral sensuality of the place or to marvel at its supernal beauty. Martin Heidegger makes note of this eerie sensibility in "), _c('i', [_vm._v("Being and Time")]), _vm._v(", for example, in which he observes that anxiety ("), _c('i', [_vm._v("Angst")]), _vm._v(") is often directly related to one’s sense of place: “In anxiety one feels ‘uncanny’ ["), _c('i', [_vm._v("unheimlich")]), _vm._v("]. Here the peculiar indefiniteness of that which Dasein finds itself alongside in anxiety, comes proximally to expression: the ‘nothing and nowhere.’ But here ‘uncanniness’ also "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_22",
      "title": "22"
    }
  }, [_vm._v("means ‘not-being-at-home’ ["), _c('i', [_vm._v("das Nicht-zu-hause-sein")]), _vm._v("].”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn17",
      "id": "fn17"
    }
  }, [_vm._v("17")])]), _vm._v(" This is an apt description of the more quotidian anxiety one associates with being lost, or even just being uncomfortable in a given place, but one can also see how even familiar places might engender in the subject feelings of fear and loathing. It is not uncommon to find oneself in a place that one finds uncomfortable or "), _c('i', [_vm._v("unheimlich")]), _vm._v(", even if it is a fairly well-known area. For example, a workplace that one loathes, a commercial district that one reluctantly visits when required to purchase goods, a locale associated with fear or anxiety (hospitals, funeral homes, cemeteries, etc.), involuntary enclosures (prison, school, factory, church), or other such undesirable places can evoke all manner of bad feelings. "), _c('i', [_vm._v("Topophobia")]), _vm._v(" seems the more apt label for the experiences so many people have with so many places."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn18",
      "id": "fn18"
    }
  }, [_vm._v("18")])]), _vm._v(" Or, perhaps, a sort of "), _c('i', [_vm._v("misotopia")]), _vm._v(" could characterize those places we hate, such as those circles of living hell comprising exurban shopping malls, mass-replicating housing developments, the desert isle or the open field, or other such unloved places. (It is also worth noting that many topophobic sites are thoroughly topophilic for those who gain pleasure from or in them; in my case, the prospect of camping in Death Valley strikes me as rather terrifying, whereas exploring strange, densely populated urban districts sounds like a delightful time.) Affective geography must account for painful or unpleasant emotional responses to places and spaces, presumably, and not limit itself to topophilic relations."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn19",
      "id": "fn19"
    }
  }, [_vm._v("19")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Proliferating neologisms aside, it appears that a crucial consideration of any properly spatial literary study is the pervasive sense, not only of place, but of place-mindedness, which characterizes both the subjective experience and the artistic representation of places, persons, events, and so forth. Fredric Jameson’s understanding of narrative as “the central function or "), _c('i', [_vm._v("instance")]), _vm._v(" of the human mind,” perhaps, is here to be supplemented with the proposition that any such narrative function be understood as itself a form of mapping, which is what I have in mind with the idea of literary cartography."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn20",
      "id": "fn20"
    }
  }, [_vm._v("20")])]), _vm._v(" Traditionally, the primacy accorded by narrative criticism and theory to the temporal dimension, with the unfolding of events over time, has sometimes left the more discernibly spatial aspects of narrative to be either ignored entirely, downplayed as a mere backdrop, or reified into a static category, as with certain variations of regionalism or nationalism that condition, if not determine, the text’s significance a priori."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn21",
      "id": "fn21"
    }
  }, [_vm._v("21")])]), _vm._v(" Yet the dynamic spatiotemporal relations among subject, situation, representation, and interpretation invite critical approaches to literature that are sensitive to the uncertain, often shifting, but always pertinent ways that place haunts the mind.")]), _vm._v(" "), _c('p', {
    staticClass: "h4"
  }, [_vm._v("The Topophrenic Condition")]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Along these lines, one might propose "), _c('i', [_vm._v("topophrenia")]), _vm._v(" as a provisional label for that condition of narrative, one that is necessary to any reading or writing of a text, in "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_23",
      "title": "23"
    }
  }, [_vm._v("which the persistence of place and of the subject’s relation to it must be constantly taken into account. Such place-mindedness is not to be understood as a simplistic relation between a given writer and his or her distinctive place (Thoreau at Walden Pond, for example), although any careful analysis of such a relationship would almost certainly disclose that things are not really so simple after all (as when, for instance, the topographic lines of Thoreau’s "), _c('i', [_vm._v("Walden")]), _vm._v(" narrative extend or reach dead ends, intersect with others, proliferate, combine, and establish new lines entirely). Rather, topophrenia suggests the degree to which all thinking is, in various ways, thinking about place, which also means thinking about the relations among places, as well as those among subjects and places, in the broadest possible sense. In practice, topophrenia represents not so much a "), _c('i', [_vm._v("geographical unconscious")]), _vm._v(" as it does an existential comportment toward the world, but it also creates problems as well as opportunities for spatial literary criticism."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn22",
      "id": "fn22"
    }
  }, [_vm._v("22")])]), _vm._v(" Topophrenia characterizes the subjective engagement with a given place, with one’s sense of place, and with the possible projection of alternative spaces. Moreover, it requires us to consider the apparently objective structures and systems that condition, not to say determine, our perceptions and experiences of space and place.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("By "), _c('i', [_vm._v("topophrenia")]), _vm._v(", of course, I mean especially a certain identifiable “place-mindedness” that informs our activities and thinking. The Greek roots of the word justify this definition, but naturally, I am aware of the pertinacious associations of the "), _c('i', [_vm._v("phrenia")]), _vm._v(" suffix with disorder, illness, or malfunction. In common parlance as well as in medical discourse, words including "), _c('i', [_vm._v("phrenia")]), _vm._v(" almost exclusively designate disease or disorder; indeed, the suffix is likely familiar to most because of the term "), _c('i', [_vm._v("schizophrenia")]), _vm._v(". In some respects, I think that this connotation is also quite suited to the idea of topophrenia as well, for I think that the pervasive place-mindedness infusing our subjective experience in and apprehension of the world is characterized by a profound sense of unease, anxiety, or discontent. Even in the more familiar or homey ("), _c('i', [_vm._v("heimlich")]), _vm._v(") zones of being-in-the-world, we are nevertheless still cognizant of, and affected by, the sense of estrangement that I take to be the persistent condition undergirding the cartographic imperative. To put it differently, even when we are “at home,” we maintain our awareness of the unfamiliar, the "), _c('i', [_vm._v("unheimlich")]), _vm._v(", and a subtle, yet visceral feeling of spatial anxiety subtends our thought and actions. In fact, this might be thought of as the fundamental impetus behind the desire for mapping, which is also the desire for narrative."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn23",
      "id": "fn23"
    }
  }, [_vm._v("23")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("If topophrenia is not a disease, properly speaking, it is a condition of “disease,” something of the sort that Freud indicates in his use of the term "), _c('i', [_vm._v("Unbehagen")]), _vm._v(", which is similar to the French term "), _c('i', [_vm._v("malaise")]), _vm._v(" and is translated as “discontent” in "), _c('i', [_vm._v("Civilization and Its Discontents")]), _vm._v(", the English title given to "), _c('i', [_vm._v("Das Unbehagen in der Kultur")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn24",
      "id": "fn24"
    }
  }, [_vm._v("24")])]), _vm._v(" Freud had originally considered "), _c('i', [_vm._v("Das Unglück in der Kultur")]), _vm._v(" as the title "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_24",
      "title": "24"
    }
  }, [_vm._v("for this book, denoting “unhappiness” in culture or civilization, but he found "), _c('i', [_vm._v("Unglück")]), _vm._v(" inapt, replacing it with "), _c('i', [_vm._v("Unbehagen")]), _vm._v(", which captures the more subtle feelings of uneasiness, discomfort, or disquiet. Along the same lines, "), _c('i', [_vm._v("topophrenia")]), _vm._v(" would not refer to an unhappiness, and its overall effects could include aspects of both topophilia and topophobia, presumably. Place-mindedness here must be understood to coincide with an entire range of affects, attitudes, conceptions, perceptions, references, and sensibilities that characterize the spatial imagination. In contrast to Tuan’s mostly sweet and light "), _c('i', [_vm._v("topophilia")]), _vm._v(", this sensibility or affect is not always pleasant, homely, or secure, but rather takes “place” in a wildly oscillatory but often systemic array of forces that determine the relationship between the subject and the social or even cosmic totality. Yet any topophrenic condition or attitude is also necessarily open to the delights of space and place, to the free play of spatial practices in which we invariably find ourselves both inscribed and inscribing, or to what Louis Marin in his remarkable study of utopia called "), _c('i', [_vm._v("jeux d’espaces")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn25",
      "id": "fn25"
    }
  }, [_vm._v("25")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The experience of a place is therefore no simple matter. Any proper orientation or “sense of place” is definitively connected to and complicated by a seemingly infinite network of spaces and places that not only serve as shifting points of view or frames of reference but also affect the situation of the subject itself. A place is apprehended subjectively, but it is also only understandable as such when located within or in reference to a non- or suprasubjective ensemble of spatial relations, sites, networks, circuits, and so on. In fact, and this brings the matter of "), _c('i', [_vm._v("literary cartography")]), _vm._v(" back into the picture for the moment, the apprehension of place is bound up in a discursive or narrative ensemble of relations that—like some metaphor out of modern physics, such as Werner Heisenberg’s uncertainty principle or Erwin Schrödinger’s wayward cats—determine the outcome by implicating the subjective perception and the objective (or nonsubjective) thing in itself within a tenuous, untrustworthy, and ever-changing system: specifically, that of language. If, as Tuan insists, a place is defined in part as a site imbued with meaning, therefore subject to interpretation and thus an appropriate subject for literary criticism, it also needs to be understood that the language used to describe and to interpret the place itself engenders or conditions the place. The place is a text, but one that is necessarily informed, and indeed formed, by other texts as well.")]), _vm._v(" "), _c('p', {
    staticClass: "indentb"
  }, [_vm._v("In "), _c('i', [_vm._v("Nausea")]), _vm._v(", quite possibly the most thoroughgoing analysis of existential anxiety in fiction, Jean-Paul Sartre’s protagonist Antoine Roquentin reflects on his time in Morocco two years earlier but concludes that he would be unable to truly apprehend or comprehend the scenes or his experiences because of the intrusive power of words, language, or narrative. “I don’t "), _c('i', [_vm._v("see")]), _vm._v(" anything anymore: I can search the past in vain, I can only find these scraps of images and I am not sure what they represent, whether they are memories or just fiction.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn26",
      "id": "fn26"
    }
  }, [_vm._v("26")])]), _vm._v(" Roquentin "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_25",
      "title": "25"
    }
  }, [_vm._v("realizes not only that the places he has been are utterly imbricated with the various memories he holds of them and the stories he has read or heard about them, such that they cannot be “known” in any reasonably objective way, but also that his own identity as the “experiencer” of these places is unstable. The person who had had experiences in Morocco or Spain or England, wherever, is already a fictional character in a story, now that the story is all there is that remains from which to construct or reconstruct the place and this now-strange person’s experience of it. As he continues,")])]), _vm._v(" "), _c('p', {
    staticClass: "blk"
  }, [_vm._v("There are many cases where even the scraps have disappeared: nothing is left but words. I could still tell stories, tell them too well . . ., but these are only the skeletons. There’s the story of a person who does this, does that, but it isn’t I, I have nothing in common with him. He travels through countries I know no more about than if I had never been there. Sometimes, in my story, it happens that I pronounce these fine names you read in atlases, Aranjuez or Canterbury. New images are born in me, images such as people create in books who have never travelled. My words are dreams, that is all."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn27",
      "id": "fn27"
    }
  }, [_vm._v("27")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("Of course, Roquentin’s anxiety need not deter anyone from engaging in a project of narrating places and experiences, as if the cartographic imperative could be so easily overruled by the mere sophistry of an angst-ridden intellectual. In fact, the discomfort or uneasiness that Sartre’s character experiences is part of the topophrenic condition. One cannot help but think of place and places, visualizing them and putting those images into some sort of narrative framework, at which point the tale and the teller become intertwined with the vast spatial ensemble that makes possible, but also makes difficult, such representations in language. Literary cartography, like “real” cartography, does not deliver the truth of the places it represents, after all. It provides more or less useful figurations by which we come to give shape to or make sense of those places and their significance.")]), _vm._v(" "), _c('p', {
    staticClass: "indentb"
  }, [_vm._v("One experiences the visceral force of this topophrenia in Wendy Chin-Tanner’s poem, “On Truth in a Nonmoral Sense.” Readers will recognize the allusion to Friedrich Nietzsche’s brief 1873 essay “On Truth and Lie in an Extra-Moral Sense” ("), _c('i', [_vm._v("Über Wahrheit und Lüge im außermoralischen Sinne")]), _vm._v("), and Chin-Tanner includes as an epigraph Nietzsche’s well-known assertion at the end of that essay that the truth is nothing more than “a mobile army of metaphors, metonyms, and anthropomorphisms—in short, a sum of human relations which have been enhanced, transposed, and embellished poetically and rhetorically, and which after long use seem firm, canonical, and obligatory to a people.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn28",
      "id": "fn28"
    }
  }, [_vm._v("28")])]), _vm._v(" In Chin-Tanner’s stunning meditation on this Nietzschean “truth,” the poet bodies forth an entire critique of the human sciences while inviting us to experience the haunting of places—which are themselves "), _c('i', [_vm._v("haunts")]), _vm._v(", of course—by a kind of irrepressible subjectivity.")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_26",
      "title": "26"
    }
  }, [_vm._v("In sociology, we say "), _c('i', [_vm._v("mapping")]), _vm._v(",")])]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("we say "), _c('i', [_vm._v("cartography")]), _vm._v(" instead")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("of "), _c('i', [_vm._v("understanding")]), _vm._v(". To profess")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("to understand, you see, is hubris.")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("I am a professional digger. I")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("should say "), _c('i', [_vm._v("excavation")]), _vm._v(" or "), _c('i', [_vm._v("archeology")])]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("instead of "), _c('i', [_vm._v("digging for the truth")]), _vm._v(",")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("which is uncouth. Which is emotional.")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("And, again, hubris. We should never say")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _c('i', [_vm._v("truth")]), _vm._v(". What is the truth, anyway? Instead,")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("we should say "), _c('i', [_vm._v("subjectivity")]), _vm._v(", as in: "), _c('i', [_vm._v("To what")])]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _c('i', [_vm._v("are we subjected")]), _vm._v("? Or: "), _c('i', [_vm._v("What is the subject")])]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _c('i', [_vm._v("of the story of your life?")]), _vm._v(" To name it,")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("I say "), _c('i', [_vm._v("loss")]), _vm._v(", I say "), _c('i', [_vm._v("yearn")]), _vm._v(", I say "), _c('i', [_vm._v("tell me")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("What else can I say? In fall, before")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("the surgery, we walked, the sky the color")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("of pigeons. I listened to you breathe, the soft")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("wheezing. I listened to the sound of your shoes")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("shuffling, crunching dead leaves into the ground.")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("I thought I would lose you. How could I betray")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("you by mapping these cities so far away:")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("Paris, Prague, Vienna, Kiev? How could")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("they hurt us? These faint cartographies")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("drawn in traces of my DNA, and names,")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("the names escaping me over time and sea")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("poetically in slant, half, off, and straight")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("rhymes. I could never escape you. Before")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("us, our name stands constant, and the City stands")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("constantly shifting, like truth. Like words and meaning,")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("making meaningless the crude facts of my making."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn29",
      "id": "fn29"
    }
  }, [_vm._v("29")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("Here, one might assert, place becomes less a locale, less a localizable or even recognizable site, and more a subtly reimagined whirlwind of affective, cognitive, and experiential data. The inability to determine the "), _c('i', [_vm._v("true")]), _vm._v(" meaning of the place is countered by the literary act of significance, of meaning-making, and the poet’s own mapping of the places help to constitute both this sense of place and that of "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_27",
      "title": "27"
    }
  }, [_vm._v("her own subjectivity. As in Nietzsche, the humbling of scientific reason does not augur some ultimately meaningless existence, but rather becomes a step toward unleashing forces of active, creative, speculative thought."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn30",
      "id": "fn30"
    }
  }, [_vm._v("30")])]), _vm._v(" The work of art, in this case, forms a map even as it implicitly criticizes the innate epistemic hubris of the cartographic endeavor. One cannot "), _c('i', [_vm._v("know")]), _vm._v(" the place, in that older sense, but one can make available a kind of knowledge of both place and self through one’s patient, meticulous, creative, and topophrenic activity. A literary cartography emerges as a figuration of subjective experience and objective space, affective geographies combined with abstract principles, forming a sort of constellation by which the poet can navigate her world . . . and, perhaps, even change it."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn31",
      "id": "fn31"
    }
  }, [_vm._v("31")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The fascination with place is a sign of our uneasy, but all-too-necessary relationship with the vicissitudes of space, as we “scan the horizon for a still spot,” as Chin-Tanner puts it in another poem."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn32",
      "id": "fn32"
    }
  }, [_vm._v("32")])]), _vm._v(" This still spot—also known as Tuan’s "), _c('i', [_vm._v("pause")]), _vm._v(", or the site that delights, disturbs, enraptures, and haunts—is the place one might give a name to, investing it with subjective meanings while subjecting it to analytical scrutiny. Caught as we are always in the middest, amid the complex and oscillatory relations of power and knowledge, the geocritic may nevertheless attempt to achieve a sense of place, so long as it is understood to be provisional, contingent, and subject to perpetual modification. Like a map, in fact.")]), _vm._v(" "), _c('p', {
    staticClass: "h4"
  }, [_vm._v("Real-and-Imagined Places")]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("In an early essay titled “Literary Geography” (1905), in which she reviewed two books (one on Charles Dickens, one on William Thackeray) in a Pilgrimage Series intended for enthusiasts who wished to engage in fiction-inspired tourism, Virginia Woolf discusses the relationship between the imaginary spaces or places depicted in a novel and the real-world places to which these appear to refer. Although she admits that certain readers may take pleasure in visiting places that, say, Dickens himself once graced and she concedes that certain biographically oriented critics could make much of how a given environment affected an author’s work, Woolf concludes that this attempt to draw meaningful connections between the literary cartography of novel and the physical geography of England is wrongheaded and futile. As she puts it, “A writer’s country is a territory within his own brain; and we run the risk of disillusionment if we try to turn such phantom cities into tangible brick and mortar. We know our way there without signposts or policemen, and we can greet the passers by without need of introduction. No city indeed is so real as this that we make for ourselves and people to our liking; and to insist that it has any counterpart in the cities of the earth is to rob it of half its charm.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn33",
      "id": "fn33"
    }
  }, [_vm._v("33")])]), _vm._v(" For Woolf, then, the reader’s desire to visit the real places that appear in the fiction of a Thackeray or a Dickens ultimately diminishes the affective power of these imaginary places. Woolf’s tone is "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_28",
      "title": "28"
    }
  }, [_vm._v("clear: only the naive and foolish should wish to visit in the flesh the places of the imagination.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Umberto Eco, a writer who is certainly neither naive nor foolish, has confessed his own “episode in literary fandom,” in which he found himself “looking for the house in Eccles Street in Dublin where Leopold Bloom is supposed to have lived.” The pilgrimage to a fictional character’s purported haunts undoubtedly satisfies a real desire for many devoted enthusiasts of the fiction, and those places take on additional meanings by virtue of their literary resonances. Nevertheless, like Woolf, Eco recognizes that “to be a good reader of Joyce, it is not necessary to celebrate Bloomsday on the banks of the Liffey.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn34",
      "id": "fn34"
    }
  }, [_vm._v("34")])]), _vm._v(" James Joyce’s Dublin is not, after all, the “real” Dublin, notwithstanding his own playful assertion that his goal in writing "), _c('i', [_vm._v("Ulysses")]), _vm._v(" was “to give a picture of Dublin so complete that if the city one day suddenly disappeared from the earth, it could be reconstructed out of my book.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn35",
      "id": "fn35"
    }
  }, [_vm._v("35")])]), _vm._v(" But, then, if "), _c('i', [_vm._v("Ulysses")]), _vm._v(" does not provide us with the picture of the “real” Dublin, neither does “the real Dublin” stand ontologically apart from its innumerable narrative representations.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This can even be the case when the geographical reference is a fictional or fantastic one. For example, without having direct access to Lewis Carroll’s rabbit hole, C. S. Lewis’s wardrobe, or J. K. Rowling’s Platform 9¾ and the Hogwarts Express train, how could we visit Wonderland, Narnia, or Hogwarts School of Witchcraft and Wizardry? And at least these places are connected to geospace of our world through such portals."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn36",
      "id": "fn36"
    }
  }, [_vm._v("36")])]), _vm._v(" Other realms, such as George R. R. Martin’s self-enclosed fantasy world containing the continents of Essos and Westeros are presumably inaccessible except through reading. And yet, not surprisingly, the same bookish wanderlust that motivated late Victorian readers to pore over "), _c('i', [_vm._v("The Dickens Country")]), _vm._v(" or "), _c('i', [_vm._v("The Thackery Country")]), _vm._v(" in search of meaningful geographical experiences and literary pilgrimages at which a young Woolf gently scoffs has created whole industries for tourism, marketing, and fan culture in the present.")]), _vm._v(" "), _c('p', {
    staticClass: "indentb"
  }, [_vm._v("For example, Northern Ireland has been experiencing a significant boost in tourism related not only to its own rich literary and cultural history but also to its connection to “the North” of Westeros in Martin’s fantasy world. Various locations represent the setting (more particularly, the film locations) in the HBO series "), _c('i', [_vm._v("Game of Thrones")]), _vm._v(", an adaptation of Martin’s possibly interminable saga "), _c('i', [_vm._v("A Song of Ice and Fire")]), _vm._v(". Traveling only a short distance from Belfast, tourists can “Visit Westeros,” taking lunch at Winterfell, practicing archery, and meeting direwolves. Likewise, those who wish to go “Beyond the Wall” can do so by signing up for tours in Iceland, where, as one website put it in highlighted bullet points, a person can:")]), _vm._v(" "), _c('p', {
    staticClass: "bull"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("•"), _c('span', {
    staticClass: "spaceb"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("Visit the areas where Night’s Watch men, including Samwell Tarly and Lord Commander Mormont, are attacked by the White Walkers.")])]), _vm._v(" "), _c('p', {
    staticClass: "bull"
  }, [_c('span', {
    staticClass: "hide"
  }), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_29",
      "title": "29"
    }
  }, [_vm._v("•"), _c('span', {
    staticClass: "spaceb"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("Walk the land that Jon Snow, Ygritte, and the Free Folk cross to reach the Wall.")])])]), _vm._v(" "), _c('p', {
    staticClass: "bull"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("•"), _c('span', {
    staticClass: "spaceb"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("Visit the cave where Jon Snow and Ygritte have their first intimate romantic encounter.")])]), _vm._v(" "), _c('p', {
    staticClass: "bull"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("•"), _c('span', {
    staticClass: "spaceb"
  }, [_c('span', {
    staticClass: "hide"
  }), _vm._v("Learn about how the Free Folk, like the ancient Vikings, lived and survived in their harsh environment."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn37",
      "id": "fn37"
    }
  }, [_vm._v("37")])])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("The blending of fact and fiction in this tour is so complete as to almost completely obscure the difference, as if we could visit an actual cave in which two fantastic characters who never existed in the real world, inhabitants of a fantasy realm called Westeros, once canoodled.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Similarly, consider the language used in promoting "), _c('i', [_vm._v("Game of Thrones")]), _vm._v(" tourism in Dubrovnik, Croatia, where many of the scenes taking place in the fictional capital King’s Landing were filmed: “Climb the old city walls that were attacked by the Baratheons in the first series, and then explore Lovrijenac Fortress—the beautiful 11th-century castle that features heavily in many of the "), _c('i', [_vm._v("Game of Thrones")]), _vm._v(" battle scenes. Gaze down at fictional Blackwater Bay from the fort’s impressive vantage point, and imagine the bloody Battle of Blackwater taking place outside of its walls.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn38",
      "id": "fn38"
    }
  }, [_vm._v("38")])]), _vm._v(" The fact that tourists would flock to these all-too-real places in order to commune with fictional characters, events, and locales is perhaps not very shocking. After all, since at least 2001 we have known that New Zealand is actually Middle-earth, on account of the tremendous success of "), _c('i', [_vm._v("The Lord of the Rings")]), _vm._v(" movie trilogy. These and other areas like them are what Rhona Trauvitch has referred to as “transontological spaces,” and because they are crucial to our topophrenic experience and worldview, they are necessarily important to any literary cartography of the world."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn39",
      "id": "fn39"
    }
  }, [_vm._v("39")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("All of this is to say that Woolf’s dichotomy of real versus imaginary places cannot entirely stand, as the imaginary London of "), _c('i', [_vm._v("Oliver Twist")]), _vm._v(" is no more a fictional production than the real (but also imagined) space of London, whose contemporary features are in some ways marked by their Dickensian character. Dickens’s London was very much a sort of representation of the London in which he lived, but it was also a space of the imagination, in which the most quotidian scenes could be charged with magic and wonder. According to a well-known anecdote related by G. K. Chesterton, a young Charles Dickens was entranced when he discovered the mystical words “MOOR EEFFOC,” but the origin of the phrase was rather commonplace; he simply read the sign “Coffee Room” from the other side of a glass door. In Chesterton’s view, this is exemplary of “the masterpiece of the good realistic principle—the principle that the most fantastic of all is often the precise fact,” and he referred to this as “that elvish kind of realism Dickens adopted everywhere.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn40",
      "id": "fn40"
    }
  }, [_vm._v("40")])]), _vm._v(" It seems likely that much of the magic and the reality that both visitors and inhabitants experience in contemporary London "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_30",
      "title": "30"
    }
  }, [_vm._v("owe something to the literary representations of its real-and-imagined spaces by authors like Dickens over the centuries.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Topophrenia, a sort of visceral and persistent place-mindedness, affects us in these places now, and the affective geography of “real-world” topoi is not lessened by the relative fictionality of their representative stories."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn41",
      "id": "fn41"
    }
  }, [_vm._v("41")])]), _vm._v(" Thus Langston Hughes’s poems may simultaneously map places like Harlem and become themselves maps, no matter how oblique or ironic, engendering the spaces they figure forth and bringing the real-and-imaginary spaces of urban experience to bear on literary representation, and vice versa. Paterson, New Jersey, from the vantage of a William Carlos Williams, is quite different from that viewed on a AAA roadmap, yet the sense of place that emerges from both a literary representation and a geographic information systems (GIS) survey can illuminate the constructedness of a place’s "), _c('i', [_vm._v("imageability")]), _vm._v(" (to use Kevin Lynch’s term), which in turn allows us to imagine otherwise. The streets, parks, and houses of Woolf’s own London become examples, in a novel such as "), _c('i', [_vm._v("Mrs. Dalloway")]), _vm._v(", not only of her vibrant spatial imagination but also of the raw materials for other literary cartographers and would-be map readers encountering those spaces afresh. This possible alterity is no more clearly sensed than in the eye (or perhaps the heart, stomach, and loins) of the exile, the expatriate who charts trajectories through a decidedly foreign yet somehow also longed-for place. The Baudelairean "), _c('i', [_vm._v("flâneur")]), _vm._v(" in Paris, aching for something always out of reach, traces an entire cartography of a city imbued with proliferating desires, luxuries, and necessities. Critical to all of these formulations is the fact that the spaces represented in the text and in the world partake of the same forms of narrative figuration. The “real” Paris, no less than the “real” Winterfell, is made of stories as much as stones. Geocritical explorations of real-and-imaginary places require readers to pay close attention to those textual spaces that contribute to making a place a place.")]), _vm._v(" "), _c('p', {
    staticClass: "h4"
  }, [_vm._v("Mapping Narratives")]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Despite the author’s stated intention “to give a picture of Dublin so complete that if the city one day suddenly disappeared from the earth, it could be reconstructed out of my book,” I would imagine that readers of Joyce’s "), _c('i', [_vm._v("Ulysses")]), _vm._v(", even those with a strong background in geography or urban planning, would find it difficult to discern the blueprint of Dublin in the text of the modernist novel. Nonetheless, the cartographic imperative broadly conceived in Joyce’s fiction is certainly apparent. From the meticulous descriptions of recognizable locales to the more implicit, affective geography of the intellectual and emotional content of the narrative, a work such as "), _c('i', [_vm._v("Ulysses")]), _vm._v(" provides readers a multimodal map of the diverse spaces represented in it. But it certainly is not just the case in Joyce’s work. In fact, although certain narratives may be more ostensibly cartographic than others, all may be said to constitute forms of literary cartography. In works "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_31",
      "title": "31"
    }
  }, [_vm._v("of fiction in which the imaginative faculty is perhaps most strongly attached to verbal narrative and description, this mapmaking project becomes central to the aims and the effects of the work. As J. Hillis Miller has put it, “A novel is a figurative mapping.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn42",
      "id": "fn42"
    }
  }, [_vm._v("42")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Speaking figuratively, then, one could agree with Peter Turchi that every writer is also in some ways a cartographer—and vice versa, perhaps. As Turchi puts it in his elegantly written and beautifully illustrated guide to creative writing, "), _c('i', [_vm._v("Maps of the Imagination")]), _vm._v(", “We organize information on maps in order to see our knowledge in a new way. As a result, maps suggest explanations; and while explanations reassure us, they also inspire us to ask more questions, consider other possibilities.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn43",
      "id": "fn43"
    }
  }, [_vm._v("43")])]), _vm._v(" In other words, maps presuppose narratives, which in turn may function as maps. The perceived tension between narration and description, like that between text and image or even between time and space, animates the form of narrative discourse, as the struggle between advancing the plot and satisfactorily sketching the scene plays itself out in a given literary work. Turchi refers to this in terms of the overlapping or entangled creative acts of "), _c('i', [_vm._v("exploration")]), _vm._v(" and "), _c('i', [_vm._v("presentation")]), _vm._v(". For the writer or literary cartographer, the imperative to arrive at some resolution of the dilemma—or at least to maintain the tension in some sort of productive equipoise—must confront the fact that all spaces and places are necessarily embedded with narratives, just as all narratives must mobilize and organize spaces and places. Thus, for example, it is not enough for Joyce to describe in minute detail the physical features of Dublin, its landscapes, streets, alleys, and houses; to reconstruct Joyce’s particular Dublin, we must discern in its unique spaces the narratives that make it a place worth taking note of in the first place, from Buck Mulligan’s bowl of lather as described in the opening pages to Molly Bloom’s “yes I said yes I will Yes” on the novel’s final page. In mapping a place, one also tells a story.")]), _vm._v(" "), _c('p', {
    staticClass: "indentb"
  }, [_vm._v("One of my favorite examples of the perhaps unresolvable problem of spatial description and temporal narration comes from Italo Calvino’s "), _c('i', [_vm._v("Invisible Cities")]), _vm._v(". In admitting his inability to describe the city of Zaira, the narrator produces an extremely evocative and meaningful picture. As Calvino’s narrator, Marco Polo, explains his dilemma,")]), _vm._v(" "), _c('p', {
    staticClass: "blk"
  }, [_vm._v("In vain, great-hearted Kublai, shall I attempt to describe Zaira, city of high bastions. I could tell you how many steps make up the streets rising like stairways, and the degree of the arcades’ curves, and what kind of zinc scales cover the roofs; but I already know this would be the same as telling you nothing. The city does not consist of this, but of relationships between the measurements of its space and the events of its past: the height of a lamppost and the distance from the ground of a hanged usurper’s swaying feet; the line strung from the lamppost to the railing opposite and the festoons that decorate the course of the queen’s nuptial procession; the height of that railing and the leap of the adulterer who climbed over it at dawn; the tilt of a guttering and "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_32",
      "title": "32"
    }
  }, [_vm._v("a cat’s progress along it as he slips into the same window; the firing range of a gunboat which has suddenly appeared beyond the cape and the bomb that destroys the guttering; the rips in the fish net and the three old men seated on the dock mending nets and telling each other for the hundredth time the story of the gunboat of the usurper, who some say was the queen’s illegitimate son, abandoned in his swaddling clothes there on the dock.")])]), _vm._v(" "), _c('p', {
    staticClass: "blki"
  }, [_vm._v("As this wave from memories flows in, the city soaks it up like a sponge and expands. A description of Zaira as it is today should contain all Zaira’s past. The city, however, does not tell its past, but contains it like the lines of a hand, written in the corners of the streets, the gratings of the windows, the banisters of the steps, the antennae of the lightning rods, the poles of the flags, every segment marked in turn with scratches, indentations, scrolls."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn44",
      "id": "fn44"
    }
  }, [_vm._v("44")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("Spatial description and historical storytelling thus merge, and then emerge, as part of a broader literary geography, which in turn becomes the ground for a writer’s own literary cartography. A place is suffused with meanings and is thus within the proper provenance of literature and of literary criticism.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Consistent with my view of literary cartography as a fundamental aspect of storytelling, I consider narratives to be, in some ways, devices or methods used to map the real-and-imagined spaces of human experience. Narratives are, in a sense, mapping machines. But narratives—like maps for that matter—never come before us in some pristine, original form. They are always and already formed by their interpretations or by the interpretative frameworks in which they have been situated or in which we, as readers, situate them."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#rfn45",
      "id": "fn45"
    }
  }, [_vm._v("45")])]), _vm._v(" Further, we cannot help but fit narratives or spatial representations into some sort of spatiotemporal context in which they make sense to us, thereby also becoming more or less useful to us, in our own attempts to give meaningful shape to the world in which we live. That is, these narratives, which are also maps, must be understood as themselves territories to be mapped. Following the trajectories of the subject and of the object, a narrative is simultaneously something that maps and something to be mapped. This dialectical tendency may not necessarily resolve itself in the unity of opposites à la Georg Wilhelm Friedrich Hegel but may maintain itself in dynamic tension, enabling new creative possibilities for both writing and reading, here understood in terms of literary cartography and geocriticism, respectively.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Ranging widely across centuries, continents, genres, and perspectives, world literature reveals the various means by which writers form literary cartographies of the worlds represented in their narratives. In reading the narrative maps produced by such a diverse array of writers, critics shine a light on the manner in which the spaces represented in those original texts become parts of the real-and-imagined places in our world, contributing to our own topophrenic activities in the here and now. Mapping narratives—that is, narratives that map, as well as our readerly activity in mapping the narratives we encounter—make possible "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_33",
      "title": "33"
    }
  }, [_vm._v("novel spaces. To be sure, Joyce’s Dublin is not the same as the “real” Irish capital, and it may be rather unlikely that that city’s geospace could be reconstructed from scratch through the careful study of Joyce’s fiction. And yet, "), _c('i', [_vm._v("Ulysses")]), _vm._v(" is indeed a map, a spatial and narrative representation of Dublin and of the modern world. The figural mapping project of the novel may disclose different ways of seeing and of experiencing the territories surveyed in the text. Through our own geocritical explorations of such literary cartographies, we come to make better sense of their distinctive spaces and places, as well as our own.")])]), _vm._v(" "), _c('p', {
    staticClass: "h4a"
  }, [_vm._v("Notes")]), _vm._v(" "), _c('p', {
    staticClass: "notes",
    attrs: {
      "id": "rfn1"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1"
    }
  }, [_vm._v("1")]), _vm._v(". Yi-Fu Tuan, "), _c('i', [_vm._v("Space and Place: The Perspective of Experience")]), _vm._v(" (Minneapolis: University of Minnesota Press, 1977), 161–162.")]), _vm._v(" "), _c('p', {
    staticClass: "notes",
    attrs: {
      "id": "rfn2"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn2"
    }
  }, [_vm._v("2")]), _vm._v(". Ibid., 6.")]), _vm._v(" "), _c('p', {
    staticClass: "notes",
    attrs: {
      "id": "rfn3"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn3"
    }
  }, [_vm._v("3")]), _vm._v(". See Stuart Elden, "), _c('i', [_vm._v("The Birth of Territory")]), _vm._v(" (Chicago: University of Chicago Press, 2014).")]), _vm._v(" "), _c('p', {
    staticClass: "notes",
    attrs: {
      "id": "rfn4"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn4"
    }
  }, [_vm._v("4")]), _vm._v(". Tim Cresswell, "), _c('i', [_vm._v("Place: A Short Introduction")]), _vm._v(" (Oxford: Blackwell, 2004), 2–3.")]), _vm._v(" "), _c('p', {
    staticClass: "notes",
    attrs: {
      "id": "rfn5"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn5"
    }
  }, [_vm._v("5")]), _vm._v(". See Georges Perec, "), _c('i', [_vm._v("Attempt at Exhausting a Place in Paris")]), _vm._v(", trans. Marc Lowenthal (Cambridge, MA: Wakefield Press, 2010).")]), _vm._v(" "), _c('p', {
    staticClass: "notes",
    attrs: {
      "id": "rfn6"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn6"
    }
  }, [_vm._v("6")]), _vm._v(". Bertrand Westphal, "), _c('i', [_vm._v("Geocriticism: Real and Fictional Spaces")]), _vm._v(", trans. Robert T. Tally Jr. (New York: Palgrave Macmillan, 2011), 117; see also my "), _c('i', [_vm._v("Spatiality")]), _vm._v(" (London: Routledge, 2013), 143–145.")]), _vm._v(" "), _c('p', {
    staticClass: "notes",
    attrs: {
      "id": "rfn7"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn7"
    }
  }, [_vm._v("7")]), _vm._v(". Westphal’s geocentric approach makes no claims to objectivity, but by insisting on multiple perspectives, it tries to register the ways in which a place has been experienced, represented, and “read” by a number of different people with different backgrounds and distinctive perspectives. A chief benefit of using multiple points of view is that this approach would minimize personal biases, offering a more well-rounded if not complete image of the place in question.")]), _vm._v(" "), _c('p', {
    staticClass: "notes",
    attrs: {
      "id": "rfn8"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn8"
    }
  }, [_vm._v("8")]), _vm._v(". Yi-Fu Tuan, "), _c('i', [_vm._v("Topophilia: A Study of Environmental Perception, Attitudes, and Values")]), _vm._v(" (New York: Columbia University Press, 1990 [orig. 1974]), 93.")]), _vm._v(" "), _c('p', {
    staticClass: "notes",
    attrs: {
      "id": "rfn9"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn9"
    }
  }, [_vm._v("9")]), _vm._v(". See Paul Rodaway, "), _c('i', [_vm._v("Sensuous Geographies: Body, Sense, and Place")]), _vm._v(" (London: Routledge, 1994).")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn10"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn10"
    }
  }, [_vm._v("10")]), _vm._v(". See Marc Augé, "), _c('i', [_vm._v("Non-Places: Introduction to an Anthropology of Supermodernity")]), _vm._v(", trans. John Howe (London: Verso, 1995).")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn11"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn11"
    }
  }, [_vm._v("11")]), _vm._v(". Siobhan Carroll, “Atopia / Non-Place,” in "), _c('i', [_vm._v("The Routledge Handbook of Literature and Space")]), _vm._v(", ed. Robert T. Tally Jr. (London: Routledge, 2017), 159, 164–165.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn12"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn12"
    }
  }, [_vm._v("12")]), _vm._v(". Dylan Trigg, “Place and Non-Place: A Phenomenological Perspective,” in "), _c('i', [_vm._v("Place, Space, and Hermeneutics")]), _vm._v(", ed. Bruce B. Janz (New York: Springer, 2017), 130.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn13"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn13"
    }
  }, [_vm._v("13")]), _vm._v(". Ibid., 138–139.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn14"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn14"
    }
  }, [_vm._v("14")]), _vm._v(". J. Nicholas Entrikin, "), _c('i', [_vm._v("The Betweenness of Place: Towards a Geography of Modernity")]), _vm._v(" (Baltimore, MD: Johns Hopkins University Press, 1991), especially 1–26.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn15"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn15"
    }
  }, [_vm._v("15")]), _vm._v(". See Yi-Fu Tuan’s “Preface to the Morningside Edition,” "), _c('i', [_vm._v("Topophilia: A Study of Environmental Perception, Attitudes, and Values")]), _vm._v(" (New York: Columbia University Press, 1990), xi–xiv.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn16"
    }
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_34",
      "title": "34"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn16"
    }
  }, [_vm._v("16")]), _vm._v(". On “cartographic anxiety,” see Derek Gregory, "), _c('i', [_vm._v("Geographical Imaginations")]), _vm._v(" (Oxford: Blackwell, 1994), 70–73.")])]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn17"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn17"
    }
  }, [_vm._v("17")]), _vm._v(". Martin Heidegger, "), _c('i', [_vm._v("Being and Time")]), _vm._v(", trans. John Macquarrie and Edward Robinson (New York: Harper and Row, 1962), 233, bracketed terms in original.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn18"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn18"
    }
  }, [_vm._v("18")]), _vm._v(". See Dylan Trigg, "), _c('i', [_vm._v("Topophobia: A Phenomenology of Anxiety")]), _vm._v(" (London: Bloomsbury, 2016).")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn19"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn19"
    }
  }, [_vm._v("19")]), _vm._v(". Of course, Yi-Fu Tuan is also well aware of this. Indeed, his "), _c('i', [_vm._v("Landscapes of Fear")]), _vm._v(" (New York: Pantheon, 1979) offers a detailed study of the ways that fearful spaces and places affect individuals and communities.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn20"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn20"
    }
  }, [_vm._v("20")]), _vm._v(". Fredric Jameson, "), _c('i', [_vm._v("The Political Unconscious: Narrative as a Social Symbolic Act")]), _vm._v(" (Ithaca, NY: Cornell University Press, 1981), 13, 123.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn21"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn21"
    }
  }, [_vm._v("21")]), _vm._v(". See Westphal, "), _c('i', [_vm._v("Geocriticism")]), _vm._v(", especially 9–36.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn22"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn22"
    }
  }, [_vm._v("22")]), _vm._v(". See Argyro Loukaki, "), _c('i', [_vm._v("The Geographical Unconscious")]), _vm._v(" (London: Ashgate, 2014).")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn23"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn23"
    }
  }, [_vm._v("23")]), _vm._v(". See Entrikin, "), _c('i', [_vm._v("The Betweenness of Place")]), _vm._v(", 133–134.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn24"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn24"
    }
  }, [_vm._v("24")]), _vm._v(". See Sigmund Freud, "), _c('i', [_vm._v("Civilization and Its Discontents")]), _vm._v(", trans. James Strachey (New York: W. W. Norton, 2010 [orig. 1930]).")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn25"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn25"
    }
  }, [_vm._v("25")]), _vm._v(". Louis Marin, "), _c('i', [_vm._v("Utopics: The Semiological Play of Textual Spaces")]), _vm._v(", trans. Robert A. Vollrath (Atlantic Highlands, NJ: Humanities Press International, 1984), xiv.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn26"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn26"
    }
  }, [_vm._v("26")]), _vm._v(". Jean-Paul Sartre, "), _c('i', [_vm._v("Nausea")]), _vm._v(", trans. Lloyd Alexander (New York: New Directions, 1964), 47–48.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn27"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn27"
    }
  }, [_vm._v("27")]), _vm._v(". Ibid., 48.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn28"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn28"
    }
  }, [_vm._v("28")]), _vm._v(". Friedrich Nietzsche, “On Truth and Lie in an Extra-Moral Sense,” in "), _c('i', [_vm._v("The Portable Nietzsche")]), _vm._v(", ed. and trans. Walter Kaufmann (New York: Viking, 1977), 46–47.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn29"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn29"
    }
  }, [_vm._v("29")]), _vm._v(". Wendy Chin-Tanner, “On Truth in a Nonmoral Sense,” available online at "), _c('a', {
    attrs: {
      "href": "http://vinylpoetry.com/volume-9/page-30/",
      "target": "_blank"
    }
  }, [_vm._v("http://vinylpoetry.com/volume-9/page-30/")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn30"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn30"
    }
  }, [_vm._v("30")]), _vm._v(". See Gilles Deleuze, "), _c('i', [_vm._v("Nietzsche and Philosophy")]), _vm._v(", trans. Hugh Tomlinson (New York: Columbia University Press, 1982); see also Daniel T. O’Hara, "), _c('i', [_vm._v("The Art of Reading as a Way of Life: On Nietzsche’s Truth")]), _vm._v(" (Evanston, IL: Northwestern University Press, 2009).")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn31"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn31"
    }
  }, [_vm._v("31")]), _vm._v(". On affective geography and poetry, see Heather H. Yeung’s "), _c('i', [_vm._v("Spatial Engagement with Poetry")]), _vm._v(" (New York: Palgrave Macmillan, 2015).")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn32"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn32"
    }
  }, [_vm._v("32")]), _vm._v(". See Wendy Chin-Tanner, “No Moon,” in "), _c('i', [_vm._v("Turn")]), _vm._v(" (Alexander, AR: Sibling Rivalry Press, 2014), 38.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn33"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn33"
    }
  }, [_vm._v("33")]), _vm._v(". Virginia Woolf, “Literary Geography,” in "), _c('i', [_vm._v("Books and Portraits: Some Further Selections from the Literary and Biographical Writings of Virginia Woolf")]), _vm._v(", ed. Mary Lyon (New York: Harcourt, Brace, Jovanovich, 1977), 158–161.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn34"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn34"
    }
  }, [_vm._v("34")]), _vm._v(". Umberto Eco, "), _c('i', [_vm._v("Six Walks in the Fictional Woods")]), _vm._v(" (Cambridge, MA: Harvard University Press, 1994), 84.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn35"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn35"
    }
  }, [_vm._v("35")]), _vm._v(". See Frank Budgen, "), _c('i', [_vm._v("James Joyce and the Making of")]), _vm._v(" Ulysses, "), _c('i', [_vm._v("and Other Writings")]), _vm._v(", ed. Clive Hart (Oxford: Oxford University Press, 1989), 69.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn36"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn36"
    }
  }, [_vm._v("36")]), _vm._v(". In fact, the actual King’s Cross station in London now has a Platform 9¾ (Hogwarts Express), complete with Harry Potter–themed gift shop, as a tourist attraction.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn37"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn37"
    }
  }, [_vm._v("37")]), _vm._v(". “Myvatn, Mystery, and Magic: "), _c('i', [_vm._v("Game of Thrones")]), _vm._v("-Themed Tour,” Iceland Travel, accessed August 16, 2017, "), _c('a', {
    attrs: {
      "href": "https://www.icelandtravel.is/tour/item700728/myvatn-mystery-magic-game-of-thrones-themed-tour-2/",
      "target": "_blank"
    }
  }, [_vm._v("https://www.icelandtravel.is/tour/item700728/myvatn-mystery-magic-game-of-thrones-themed-tour-2/")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn38"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn38"
    }
  }, [_vm._v("38")]), _vm._v(". “Viator Exclusive: Game of Thrones’ Tour,” Lonely Planet, accessed August 16, 2017, "), _c('a', {
    attrs: {
      "href": "https://www.lonelyplanet.com/croatia/southern-dalmatia/activities/viator-exclusive-game-of-thrones-tour/a/pa-act/v-5360GAMETHRONES/1319358",
      "target": "_blank"
    }
  }, [_vm._v("https://www.lonelyplanet.com/croatia/southern-dalmatia/activities/viator-exclusive-game-of-thrones-tour/a/pa-act/v-5360GAMETHRONES/1319358")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn39"
    }
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_35",
      "title": "35"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn39"
    }
  }, [_vm._v("39")]), _vm._v(". See Rhona Trauvitch, “Charting the Extraordinary: Sentient and Transontological Spaces,” in "), _c('i', [_vm._v("Literary Cartographies: Spatiality, Representation, and Narrative")]), _vm._v(", ed. Robert T. Tally Jr. (New York: Palgrave Macmillan, 2014), 199–213.")])]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn40"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn40"
    }
  }, [_vm._v("40")]), _vm._v(". G. K. Chesterton, "), _c('i', [_vm._v("Charles Dickens: A Critical Study")]), _vm._v(" (New York: Dodd Mead and Co., 1906), 47–48.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn41"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn41"
    }
  }, [_vm._v("41")]), _vm._v(". This is related in part to what Edward W. Soja called “real-and-imagined places,” as he attempted to break down the dichotomy between the perceived or real spaces and the conceived or imagined spaces in his innovative conception of “thirdspace.” See his "), _c('i', [_vm._v("Thirdspace: Journeys to Los Angeles and Other Real-and-Imagined Places")]), _vm._v(" (Oxford: Blackwell, 1996).")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn42"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn42"
    }
  }, [_vm._v("42")]), _vm._v(". J. Hillis Miller, "), _c('i', [_vm._v("Topographies")]), _vm._v(" (Stanford, CA: Stanford University Press, 1995), 19.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn43"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn43"
    }
  }, [_vm._v("43")]), _vm._v(". Peter Turchi, "), _c('i', [_vm._v("Maps of the Imagination: The Writer as Cartographer")]), _vm._v(" (San Antonio, TX: Trinity University Press, 2004), 11.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn44"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn44"
    }
  }, [_vm._v("44")]), _vm._v(". Italo Calvino, "), _c('i', [_vm._v("Invisible Cities")]), _vm._v(", trans. William Weaver (New York: Harcourt, 1974), 10–11.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "rfn45"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn45"
    }
  }, [_vm._v("45")]), _vm._v(". See Jameson, "), _c('i', [_vm._v("The Political Unconscious")]), _vm._v(", 9–10.")])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-42ca8568","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/text/9780253037695_Chapter_1.vue
// module id = 87
// module chunks = 0